/**
 * Language Test Types & Utilities
 *
 * Shared types and helper functions for language test components.
 */

import type { TFunction } from 'i18next';
import { Book, CheckCircle2, Edit, Headphones, Mic } from '@tonnie/assets';
import {
  LANGUAGE_CERTIFICATE_MAX_PAGES,
  isLanguageTestLanguage,
  type LanguageTestFailureReason,
  type LanguageTestLanguage,
} from '@tonnie/heroes';

export interface ILanguageTestConfig {
  id: string;
  name: string;
  description?: string;
  instructions: string[];
  testUrl?: string;
  extensionUrl?: string;
  sampleImagePath?: string;
  uploadEndpoint?: {
    type: string;
    parameters?: Record<string, unknown>;
  };
}

export interface ILanguageTestWidgetProps {
  config: {
    language: string;
    tests: ILanguageTestConfig[];
    requireAllTests?: boolean;
    /** Talent id injected by the host (CoPilot engine var / TestIt 'me' sentinel). */
    talentId?: string;
    /**
     * Where a completed run submits (BL-214). Default/absent = `'checkup'`
     * (byte-identical CheckUp behaviour). `'testit'` also banks the resulting
     * CEFR to the reusable `talent_test_results` store (source `testit`).
     */
    submissionMode?: 'checkup' | 'testit';
    /** Catalog test id to bank under in testit mode (`language.en` / `language.pt`). */
    testitTestId?: string;
  };
  onComplete: () => void;
  onCancel?: () => void;
  onTestComplete?: (testId: string, testName: string) => void;
  isCompletedFeature?: boolean;
  stateId?: string;
}

/**
 * The language a `language_test` widget certifies, from its guide config. It is
 * sent to the analyzer as-is and never defaulted (H16), so a config naming any
 * other language is a defect to surface loudly — not a certificate to read as
 * English.
 */
export function requireCertifiedLanguage(configured: string): LanguageTestLanguage {
  if (!isLanguageTestLanguage(configured)) {
    throw new Error(`language_test widget configured with unknown language '${configured}'`);
  }
  return configured;
}

/**
 * One sub-test's persisted completion. JSON-shaped: it round-trips through the
 * server's `widgetStates` jsonb, so rows written by older builds are read too —
 * including `{ isCompleted: true }` with NO level, which is what a failed
 * analysis used to write (H15).
 */
export interface PersistedTestCompletion {
  isCompleted: boolean;
  cefrLevel?: string;
}

/**
 * The CEFR level a sub-test was assessed at — or null when it was not.
 *
 * A sub-test is complete only WITH a level. A persisted `isCompleted: true`
 * without one is the record of a failed analysis that used to be saved as a
 * success and rendered as a green "Completed"; it reads as not done, so the
 * citizen can upload again instead of believing the certificate was accepted.
 */
export function assessedLevel(entry: PersistedTestCompletion | undefined): string | null {
  return entry?.isCompleted === true && entry.cefrLevel ? entry.cefrLevel : null;
}

// ── Stepper types ──────────────────────────────────────────────────────

export type LanguageTestStep = 'instructions' | 'takeTest' | 'upload' | 'confirm';

export const STEP_ORDER: LanguageTestStep[] = ['instructions', 'takeTest', 'upload', 'confirm'];

export interface CEFRResult {
  overall?: string;
  speaking?: string;
  reading?: string;
  writing?: string;
  listening?: string;
}

/**
 * Why a certificate yielded no level: the analyzer's typed reason, or
 * `request_failed` when the request itself failed and no analyzer answered.
 */
export type LanguageTestFailure = LanguageTestFailureReason | 'request_failed';

/** The certificate step's state machine — every outcome distinct, no "done" that hides a failure. */
export type AnalysisState =
  | { status: 'idle' }
  | { status: 'uploading' }
  | { status: 'analyzing'; fileId: string }
  | {
      status: 'analyzed';
      fileId: string;
      cefrResult: CEFRResult;
      /** The level the sub-test is completed with — always present. */
      level: string;
      confidence: number;
    }
  | { status: 'failed'; fileId: string; reason: LanguageTestFailure }
  | { status: 'upload_error' };

/**
 * The one level a sub-test completes with: the overall band, else the first
 * skill band the certificate states. A result with no band at all is not a
 * result.
 */
export function primaryCefrLevel(cefr: CEFRResult): string | null {
  return cefr.overall ?? cefr.speaking ?? cefr.reading ?? cefr.listening ?? cefr.writing ?? null;
}

/**
 * The citizen-facing explanation for each failure — what went wrong with THIS
 * file and what to do about it. Exhaustive: a new reason does not compile
 * until it has words. Causes that are ours, not the citizen's, share one
 * "try again later" message. Literal `t()` calls, so the key-usage gate sees
 * every key.
 */
export function languageTestFailureMessage(reason: LanguageTestFailure, t: TFunction): string {
  switch (reason) {
    case 'language_mismatch':
      return t('checkup.languageTests.failed.languageMismatch');
    case 'unsupported_document_type':
    case 'document_unreadable':
      return t('checkup.languageTests.failed.unsupportedDocument');
    case 'document_too_many_pages':
      return t('checkup.languageTests.failed.tooManyPages', {
        max: LANGUAGE_CERTIFICATE_MAX_PAGES,
      });
    case 'document_archive_too_large':
      return t('checkup.languageTests.failed.archiveTooLarge');
    case 'docx_without_text':
      return t('checkup.languageTests.failed.docxEmpty');
    case 'no_cefr_found':
      return t('checkup.languageTests.failed.noLevelFound');
    case 'no_input':
    case 'storage_fetch_failed':
    case 'invalid_model_response':
    case 'provider_rejected':
    case 'provider_unavailable':
    case 'analyzer_unreachable':
    case 'internal_error':
    case 'request_failed':
      return t('checkup.languageTests.failed.serviceUnavailable');
    default: {
      const exhaustive: never = reason;
      return exhaustive;
    }
  }
}

/**
 * The citizen-facing name of a CEFR band ("Upper Intermediate" for B2), in the
 * viewer's language — null for a band this does not name. Literal `t()`
 * calls, so the key-usage gate sees every key. These were hard-coded English
 * strings, so every non-English citizen read their level's name in English.
 */
export function cefrLevelLabel(level: string, t: TFunction): string | null {
  switch (level.toUpperCase()) {
    case 'A1':
      return t('checkup.languageTests.cefrLevel.a1');
    case 'A2':
      return t('checkup.languageTests.cefrLevel.a2');
    case 'B1':
      return t('checkup.languageTests.cefrLevel.b1');
    case 'B2':
      return t('checkup.languageTests.cefrLevel.b2');
    case 'C1':
      return t('checkup.languageTests.cefrLevel.c1');
    case 'C2':
      return t('checkup.languageTests.cefrLevel.c2');
    case 'NATIVE':
      return t('checkup.languageTests.cefrLevel.native');
    default:
      return null;
  }
}

/** CEFR bands, lowest → highest, for choosing a combined `overall` band. */
const CEFR_ASCENDING = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

/**
 * Merge each language sub-test's band into ONE combined CEFR result
 * (`{ overall, speaking, reading }`) for the reusable `talent_test_results` store
 * (BL-214). The reusable unit is the whole language test, not each skill (see the
 * `@tonnie/heroes` test-catalog design note) — so a standalone TestIt run banks a
 * single row per language, not one per skill. `speaking` comes from the speaking
 * sub-test, `reading` from the reading/listening sub-test, and `overall` is the
 * highest band captured. Returns an all-`undefined` object when no band exists yet.
 */
export function combineCefrFromSkills(
  tests: Array<{ id: string }>,
  completed: Record<string, { cefrLevel?: string } | undefined>
): { overall?: string; speaking?: string; reading?: string } {
  let overall: string | undefined;
  let speaking: string | undefined;
  let reading: string | undefined;

  for (const tc of tests) {
    const band = completed[tc.id]?.cefrLevel;
    if (!band) continue;
    const id = tc.id.toLowerCase();
    if (id.includes('speaking')) speaking = band;
    else if (id.includes('reading') || id.includes('listening')) reading = band;
    // Highest band wins as the combined overall; an unknown band (rank -1) only
    // wins when nothing has been captured yet.
    const rank = CEFR_ASCENDING.indexOf(band as (typeof CEFR_ASCENDING)[number]);
    const overallRank = overall
      ? CEFR_ASCENDING.indexOf(overall as (typeof CEFR_ASCENDING)[number])
      : -Infinity;
    if (!overall || rank > overallRank) overall = band;
  }

  return { overall, speaking, reading };
}

export function getTestIcon(testId: string) {
  const id = testId.toLowerCase();
  if (id.includes('speaking')) return Mic;
  if (id.includes('reading') || id.includes('listening')) return Book;
  if (id.includes('writing')) return Edit;
  if (id.includes('listening')) return Headphones;
  return CheckCircle2;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toUpperCase() || '';
}
